import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowUpRight,
  ShieldCheck,
  Stethoscope,
  Building2,
  CalendarRange,
  Users,
  CreditCard,
  BarChart3,
  Smartphone,
  Video,
  FlaskConical,
  Pill,
  Languages,
  BrainCircuit,
  Check,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { useAuth, roleHome } from "../lib/auth";
import AppLogo from "../components/AppLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  {
    icon: Building2,
    title: "Hospital Management",
    text: "Manage hospitals, doctors, schedules, appointments, settings, branding and operational reports from one place.",
  },
  {
    icon: Stethoscope,
    title: "Doctor Portal",
    text: "Daily schedules, patient history, consultation workflows, availability and time-off management.",
  },
  {
    icon: Users,
    title: "Patient Portal",
    text: "Find hospitals and doctors, book appointments, receive notifications and manage healthcare activity.",
  },
  {
    icon: CalendarRange,
    title: "Smart Scheduling",
    text: "Weekly availability, blocked dates, appointment slots and real-time booking coordination.",
  },
  {
    icon: CreditCard,
    title: "Payments & Billing",
    text: "Track payment activity, online transactions, invoices and future billing workflows.",
  },
  {
    icon: BarChart3,
    title: "Reports & Analytics",
    text: "Monitor appointments, hospital growth, users, revenue and operational performance.",
  },
];

const FUTURE_FEATURES = [
  {
    icon: Video,
    title: "Video Consultation",
  },
  {
    icon: FlaskConical,
    title: "Laboratory Management",
  },
  {
    icon: Pill,
    title: "Pharmacy Management",
  },
  {
    icon: BrainCircuit,
    title: "AI Clinical Assistant",
  },
  {
    icon: Smartphone,
    title: "Mobile Applications",
  },
  {
    icon: Languages,
    title: "Multi-language Support",
  },
];

const PLATFORM_BENEFITS = [
  "Cloud-based platform",
  "Role-based secure access",
  "Multi-hospital architecture",
  "Mobile-friendly experience",
  "Real-time notifications",
  "White-label hospital branding",
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apiStatus, setApiStatus] = useState("checking");

  useEffect(() => {
    if (!BACKEND_URL) {
      setApiStatus("offline");
      return;
    }

    fetch(`${BACKEND_URL}/api/health`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Health check failed");
        }

        return response.json();
      })
      .then((data) => {
        setApiStatus(data.status === "ok" ? "online" : "degraded");
      })
      .catch(() => {
        setApiStatus("offline");
      });
  }, []);

  const openDashboard = () => {
    if (user) {
      navigate(roleHome(user.role));
    }
  };

  return (
    <div
      data-testid="landing-page"
      className="relative min-h-screen overflow-hidden bg-mb-bg text-white"
    >
      <div className="pointer-events-none absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-teal-500/10 blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-[480px] w-[480px] rounded-full bg-teal-400/5 blur-[160px]" />

      <header className="relative z-10 flex items-center justify-between border-b border-mb-border px-6 py-4 lg:px-12">
        <AppLogo
          to="/"
          subtitle="Smart Clinic • Easy Care"
          size={48}
        />

        <div className="flex items-center gap-4">
          <div className="hidden items-center gap-6 text-xs uppercase tracking-[0.2em] text-mb-fg2 md:flex">
            <span
              className="flex items-center gap-2"
              data-testid="api-status"
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  apiStatus === "online"
                    ? "bg-teal-400"
                    : apiStatus === "checking"
                      ? "animate-pulse bg-amber-400"
                      : "bg-red-500"
                }`}
              />

              API · {apiStatus}
            </span>
          </div>

          {user ? (
            <Button
              data-testid="dashboard-button"
              onClick={openDashboard}
              className="h-10 rounded-lg bg-teal-500 px-5 font-medium text-zinc-950 hover:bg-teal-400"
            >
              Open dashboard
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Link to="/login">
              <Button
                data-testid="signin-link"
                className="h-10 rounded-lg bg-teal-500 px-5 font-medium text-zinc-950 hover:bg-teal-400"
              >
                Sign in
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          )}
        </div>
      </header>

      <main className="relative z-10">
        <section className="mx-auto max-w-7xl px-6 pb-24 pt-20 lg:px-12 lg:pt-28">
          <div className="max-w-4xl">
            <div className="mb-8 text-[10px] uppercase tracking-[0.4em] text-teal-400">
              ◖ Healthcare Management Platform
            </div>

            <h1
              className="text-5xl font-light leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl"
              data-testid="hero-headline"
            >
              Smarter hospital operations.
              <br />
              <span className="text-teal-300">
                Better patient care.
              </span>
            </h1>

            <p className="mt-8 max-w-2xl text-base leading-relaxed text-mb-fg2 sm:text-lg">
              MediBooks connects hospitals, doctors and patients through one
              secure, cloud-based healthcare platform — from appointment
              scheduling and hospital administration to reports, payments and
              future clinical workflows.
            </p>

            <div className="mt-12 flex flex-wrap gap-3">
              <Link to="/signup/hospital">
                <Button
                  data-testid="cta-register-hospital"
                  className="h-12 rounded-lg bg-teal-500 px-6 font-medium text-zinc-950 hover:bg-teal-400"
                >
                  Register your hospital
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>

              <Link to="/signup/patient">
                <Button
                  data-testid="cta-register-patient"
                  variant="outline"
                  className="h-12 rounded-lg border-mb-border bg-transparent px-6 text-white hover:bg-mb-surface2"
                >
                  Create patient account
                </Button>
              </Link>

              <Link to="/login">
                <Button
                  data-testid="cta-signin"
                  variant="ghost"
                  className="h-12 rounded-lg px-6 text-white hover:bg-mb-surface2"
                >
                  Sign in
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-20 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-mb-border bg-mb-border md:grid-cols-3">
            <HighlightTile
              icon={<ShieldCheck className="h-5 w-5" />}
              label="Secure Platform"
              text="JWT authentication, role-based access and hospital-scoped data protection."
            />

            <HighlightTile
              icon={<Building2 className="h-5 w-5" />}
              label="Multi-Hospital"
              text="Built for clinics, hospitals and future enterprise healthcare groups."
            />

            <HighlightTile
              icon={<CalendarRange className="h-5 w-5" />}
              label="Smart Scheduling"
              text="Doctor availability, blocked dates and appointment coordination in one workflow."
            />
          </div>
        </section>

        <section className="border-y border-mb-border bg-mb-surface/40">
          <div className="mx-auto max-w-7xl px-6 py-20 lg:px-12">
            <div className="max-w-2xl">
              <div className="text-[10px] uppercase tracking-[0.35em] text-teal-400">
                Core Capabilities
              </div>

              <h2 className="mt-4 text-3xl font-light tracking-tight sm:text-4xl">
                Everything needed to run a modern healthcare practice.
              </h2>

              <p className="mt-4 text-sm leading-relaxed text-mb-fg2">
                MediBooks brings essential hospital operations into one
                connected platform with dedicated experiences for every role.
              </p>
            </div>

            <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map((feature) => (
                <FeatureCard
                  key={feature.title}
                  icon={feature.icon}
                  title={feature.title}
                  text={feature.text}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-2 lg:px-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-teal-400">
              Why MediBooks
            </div>

            <h2 className="mt-4 text-3xl font-light tracking-tight sm:text-4xl">
              Built for healthcare teams, not complicated IT departments.
            </h2>

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-mb-fg2">
              The platform is designed to stay simple for small clinics while
              supporting the security, branding and operational needs of
              growing hospitals.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {PLATFORM_BENEFITS.map((benefit) => (
              <div
                key={benefit}
                className="flex items-center gap-3 rounded-xl border border-mb-border bg-mb-surface p-4 text-sm text-mb-fg2"
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-500/10 text-teal-300">
                  <Check className="h-4 w-4" />
                </div>

                {benefit}
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-mb-border bg-mb-surface/40">
          <div className="mx-auto max-w-7xl px-6 py-20 lg:px-12">
            <div className="max-w-2xl">
              <div className="text-[10px] uppercase tracking-[0.35em] text-teal-400">
                Future Ready
              </div>

              <h2 className="mt-4 text-3xl font-light tracking-tight sm:text-4xl">
                Designed to grow into a complete healthcare operating platform.
              </h2>

              <p className="mt-4 text-sm leading-relaxed text-mb-fg2">
                Upcoming modules will extend MediBooks beyond appointments and
                administration into connected clinical and patient-care
                experiences.
              </p>
            </div>

            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {FUTURE_FEATURES.map((feature) => (
                <div
                  key={feature.title}
                  className="flex items-center gap-4 rounded-xl border border-mb-border bg-mb-surface p-5"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-500/10 text-teal-300">
                    <feature.icon className="h-5 w-5" />
                  </div>

                  <div>
                    <div className="text-sm font-medium text-white">
                      {feature.title}
                    </div>

                    <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-mb-fg2">
                      Coming Soon
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-20 lg:px-12">
          <div className="rounded-2xl border border-teal-500/20 bg-teal-500/5 px-6 py-12 text-center sm:px-12">
            <h2 className="text-3xl font-light tracking-tight">
              Ready to modernize your clinic or hospital?
            </h2>

            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-mb-fg2">
              Start with appointment management today and grow into a complete
              connected healthcare platform.
            </p>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/signup/hospital">
                <Button className="h-11 rounded-lg bg-teal-500 px-6 font-medium text-zinc-950 hover:bg-teal-400">
                  Register your hospital
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>

              <Link to="/login">
                <Button
                  variant="outline"
                  className="h-11 rounded-lg border-mb-border bg-transparent px-6 text-white hover:bg-mb-surface2"
                >
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-mb-border px-6 py-8 lg:px-12">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 text-xs text-mb-fg2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-medium text-white">
              MediBooks
            </div>

            <div className="mt-1">
              Smart Clinic • Easy Care
            </div>
          </div>

          <div className="text-left sm:text-right">
            <div>© 2026 MediBooks</div>
            <div className="mt-1">
              A Product of MNV Solutions
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function HighlightTile({ icon, label, text }) {
  return (
    <div className="bg-mb-surface p-7 transition-colors duration-300 hover:bg-[#101110]">
      <div className="flex items-center justify-between text-teal-400">
        {icon}

        <span className="text-[10px] uppercase tracking-[0.3em]">
          {label}
        </span>
      </div>

      <p className="mt-6 text-sm leading-relaxed text-mb-fg2">
        {text}
      </p>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, text }) {
  return (
    <div className="rounded-xl border border-mb-border bg-mb-surface p-6 transition-colors hover:border-teal-500/20 hover:bg-mb-surface2">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-500/10 text-teal-300">
        <Icon className="h-5 w-5" />
      </div>

      <h3 className="mt-5 text-base font-medium text-white">
        {title}
      </h3>

      <p className="mt-3 text-sm leading-relaxed text-mb-fg2">
        {text}
      </p>
    </div>
  );
}